import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Database } from '@/integrations/supabase/types';

type ParserBotSupplier = Database['public']['Tables']['parser_bot_suppliers']['Row'];

export const useParserBotSuppliers = () => {
  const queryClient = useQueryClient();

  // Get all suppliers
  const { data: suppliers, isLoading } = useQuery({
    queryKey: ['parser-bot-suppliers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('parser_bot_suppliers')
        .select('*')
        .order('supplier_name');

      if (error) throw error;
      return data;
    },
  });

  // Toggle single supplier
  const toggleSupplier = useMutation({
    mutationFn: async (params: { supplierId: string; enabled: boolean }) => {
      const { error } = await supabase
        .from('parser_bot_suppliers')
        .update({ is_enabled: params.enabled })
        .eq('id', params.supplierId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parser-bot-suppliers'] });
      toast.success('Supplier updated');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update supplier');
    },
  });

  // Bulk toggle suppliers
  const bulkToggle = useMutation({
    mutationFn: async (params: { supplierIds: string[]; enabled: boolean }) => {
      const { error } = await supabase
        .from('parser_bot_suppliers')
        .update({ is_enabled: params.enabled })
        .in('id', params.supplierIds);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['parser-bot-suppliers'] });
      toast.success(`${variables.supplierIds.length} suppliers updated`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update suppliers');
    },
  });

  // Enable all suppliers
  const enableAll = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('parser_bot_suppliers')
        .update({ is_enabled: true })
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Update all

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parser-bot-suppliers'] });
      toast.success('All suppliers enabled');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to enable all suppliers');
    },
  });

  // Disable all suppliers
  const disableAll = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('parser_bot_suppliers')
        .update({ is_enabled: false })
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Update all

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parser-bot-suppliers'] });
      toast.success('All suppliers disabled');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to disable all suppliers');
    },
  });

  return {
    suppliers: suppliers || [],
    isLoading,
    toggleSupplier,
    bulkToggle,
    enableAll,
    disableAll,
  };
};
